import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/sonner';
import { FieldError, FieldLabel, TextField } from '@/components/FormField';
import { isValidEmail } from '@/lib/validation';
import { useCreateUser, useUpdateUser, type AdminUser, type UserRole } from './api';

type Mode = { kind: 'create' } | { kind: 'edit'; user: AdminUser };

type FieldKey = 'email' | 'firstName' | 'lastName';

type FieldErrors = Partial<Record<FieldKey, string>>;

const FOCUS_ORDER: Array<[FieldKey, string]> = [
  ['email', 'user-email'],
  ['firstName', 'user-first-name'],
  ['lastName', 'user-last-name'],
];

function focusFirstError(errors: FieldErrors): void {
  for (const [key, id] of FOCUS_ORDER) {
    if (errors[key]) {
      document.getElementById(id)?.focus();
      return;
    }
  }
}

export function UserFormDialog({ mode, children }: { mode: Mode; children: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isEdit = mode.kind === 'edit';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('admin.users.edit_title') : t('admin.users.create_title')}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? t('admin.users.edit_description') : t('admin.users.create_description')}
          </DialogDescription>
        </DialogHeader>
        <UserForm mode={mode} onDone={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}

function UserForm({ mode, onDone }: { mode: Mode; onDone: () => void }) {
  const { t } = useTranslation();
  const isEdit = mode.kind === 'edit';
  const initial = mode.kind === 'edit' ? mode.user : null;

  const [email, setEmail] = useState(initial?.email ?? '');
  const [firstName, setFirstName] = useState(initial?.firstName ?? '');
  const [lastName, setLastName] = useState(initial?.lastName ?? '');
  const [role, setRole] = useState<UserRole>(initial?.role ?? 'USER');
  const [showErrors, setShowErrors] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);

  const create = useCreateUser();
  const update = useUpdateUser();
  const pending = create.isPending || update.isPending;

  const computeErrors = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (!isEdit) {
      if (email.trim() === '') errors.email = 'admin.users.validation.email_required';
      else if (!isValidEmail(email)) errors.email = 'admin.users.validation.email_invalid';
    }
    if (firstName.trim() === '') errors.firstName = 'admin.users.validation.first_name_required';
    if (lastName.trim() === '') errors.lastName = 'admin.users.validation.last_name_required';
    return errors;
  };

  const errors: FieldErrors = showErrors ? computeErrors() : {};
  if (emailTaken) errors.email = errors.email ?? 'admin.users.validation.email_taken';

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (pending) return;
    setEmailTaken(false);
    const found = computeErrors();
    if (Object.keys(found).length > 0) {
      setShowErrors(true);
      focusFirstError(found);
      return;
    }
    setShowErrors(false);
    if (mode.kind === 'edit') {
      update.mutate(
        {
          id: mode.user.id,
          input: { firstName: firstName.trim(), lastName: lastName.trim(), role },
        },
        {
          onSuccess: () => {
            toast.success(t('admin.users.toasts.updated'));
            onDone();
          },
          onError: () => toast.error(t('admin.users.toasts.update_failed')),
        },
      );
    } else {
      create.mutate(
        { email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim(), role },
        {
          onSuccess: () => {
            toast.success(t('admin.users.toasts.created'));
            onDone();
          },
          onError: (error) => {
            if (error.code === 'EMAIL_TAKEN') {
              setEmailTaken(true);
              document.getElementById('user-email')?.focus();
              return;
            }
            toast.error(t('admin.users.toasts.create_failed'));
          },
        },
      );
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="space-y-1.5">
        <FieldLabel htmlFor="user-email" required={!isEdit}>
          {t('admin.users.fields.email')}
        </FieldLabel>
        <Input
          id="user-email"
          type="email"
          value={email}
          disabled={isEdit}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailTaken(false);
          }}
          autoComplete="off"
          aria-required={!isEdit || undefined}
          aria-invalid={errors.email ? true : undefined}
          aria-describedby={errors.email ? 'user-email-error' : undefined}
        />
        {errors.email ? (
          <FieldError id="user-email-error" message={t(errors.email)} />
        ) : isEdit ? (
          <p className="text-xs text-muted-foreground">{t('admin.users.email_locked_hint')}</p>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          id="user-first-name"
          label={t('admin.users.fields.first_name')}
          required
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          error={errors.firstName ? t(errors.firstName) : undefined}
        />
        <TextField
          id="user-last-name"
          label={t('admin.users.fields.last_name')}
          required
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          error={errors.lastName ? t(errors.lastName) : undefined}
        />
      </div>

      <div className="space-y-1.5">
        <Label>{t('admin.users.fields.role')}</Label>
        <RoleToggle value={role} onChange={setRole} disabled={pending} />
      </div>

      <Separator />

      <Button type="submit" className="w-full" disabled={pending}>
        {pending ? <Loader2 className="animate-spin" /> : null}
        {isEdit ? t('admin.users.save') : t('admin.users.add')}
      </Button>
    </form>
  );
}

const ROLES: readonly UserRole[] = ['USER', 'ADMIN'];

function RoleToggle({
  value,
  onChange,
  disabled,
}: {
  value: UserRole;
  onChange: (role: UserRole) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-2" role="radiogroup">
      {ROLES.map((option) => {
        const active = option === value;
        return (
          <Button
            key={option}
            type="button"
            role="radio"
            aria-checked={active}
            variant={active ? 'secondary' : 'outline'}
            disabled={disabled}
            onClick={() => onChange(option)}
          >
            {t(`admin.users.role.${option}`)}
          </Button>
        );
      })}
    </div>
  );
}
